import * as _ from 'lodash'
import React from 'react';
import jobCostNoteFactory from '../models/jobCostNoteModel';
import jobCostScheduleItemFactory from '../models/jobCostScheduleItemModel';
import componentServices from '../componentServices';
import * as contractorActions from '../contractorActions';
import * as employeeActions from '../employeeActions';
import * as reportActions from '../reportActions';
import { searchJobs } from '../jobActions';
import { pushAlert } from '../actions';
import enums from '../enum';
import util from '../util';
import valueFilters from '../valueFilters';
import ScopeOfWork from '../models/scopeOfWork';
import { default as globalRelay, WellKnownGlobalFunction} from '../globalRelay';
import { AsyncButton, Radio } from './sharedComponents';
import store, { getCollection, getModel, ModelCollection } from 'Scripts/store';

const formatContact = contact => {
	const parts: string[] = [];

	if (contact) {
		if (contact.fullName) {
			parts.push(contact.fullName);
		}
		if (contact.preferredContactMethodValue) {
			parts.push(contact.preferredContactMethodValue);
		}
	}

	return parts.length ? parts.join(' - ') : '';
};

const formatAddress = address => {
	return address ? `${address.street}, ${address.city}, ${address.region} ${address.postalCode}` : '';
};

const JobCostReviewContract = ({ accountingIdRow, approveRow, accountManagerName, builder, coordinatorName, firstJobContract, installerNote, job, jobTypeName, operationsNote, proposedDeliverDate, rejectionNoteRow, summary, submitButton }: any) => {
	const budgetValues = summary.extensionItems.filter(item => item.group === 'accounting');
	const firstContractLabel = `Original ${globalRelay.config!.getString('Accounting ID', 'job')}`;
	const budgetValueRows = _.map(budgetValues || [], (v, i) => (<tr key={i}>
		<td />
		<td><strong>{v.itemNote}</strong></td>
		<td>{util.formatAsCurrency(v.type === 'budget-cost' ? v.cost : v.sellPrice)}</td>
		<td />
	</tr>));
	const builderRows = builder ? [<tr key="builder-name">
		<td />
		<td><strong>Builder</strong></td>
		<td>{builder.name}</td>
		<td />
	</tr>,
	<tr key="builder-contact">
		<td />
		<td><strong>Builder Primary Contact</strong></td>
		<td>{formatContact(builder.primaryContact)}</td>
		<td />
	</tr>] : null;

	return (
		<section className="jobCostFinalize">
			{ /* Final Details */}
			<section>
				<header>
					<span>REVIEW</span>
				</header>
				<div className="inner compact">
					<table cellPadding="0" cellSpacing="0">
						<tbody>
							<tr>
								<td />
								<td><strong>Job Type</strong></td>
								<td>{jobTypeName}</td>
								<td />
							</tr>
							<tr>
								<td />
								<td><strong>Designer</strong></td>
								<td>{accountManagerName}</td>
								<td />
							</tr>
							<tr>
								<td />
								<td><strong>Coordinator</strong></td>
								<td>{coordinatorName}</td>
								<td />
							</tr>
							{builderRows}
							<tr>
								<td />
								<td><strong>Client Primary Contact</strong></td>
								<td>{formatContact(job.primaryClientContact)}</td>
								<td />
							</tr>
							<tr>
								<td />
								<td><strong>Billing Address</strong></td>
								<td>{formatAddress(job.billingAddress)}</td>
								<td />
							</tr>
							<tr>
								<td />
								<td><strong>Delivery Address</strong></td>
								<td>{formatAddress(job.workSiteAddress)}</td>
								<td />
							</tr>
							<tr>
								<td />
								<td><strong>Proposed Delivery Date</strong></td>
								<td>
									{proposedDeliverDate}
								</td>
								<td />
							</tr>
							<tr>
								<td />
								<td><strong>Notes for Operations</strong></td>
								<td>{operationsNote}</td>
								<td />
							</tr>
							<tr>
								<td />
								<td><strong>Notes for Installer</strong></td>
								<td>{installerNote}</td>
								<td />
							</tr>
							{budgetValueRows}
							{!firstJobContract ? null : <tr>
								<td />
								<td><strong>{firstContractLabel}</strong></td>
								<td>{firstJobContract.accountingId}</td>
								<td />
							</tr>}
							{accountingIdRow}
							{approveRow}
							{rejectionNoteRow}
							<tr>
								<td />
								<td />
								<td>{submitButton}</td>
								<td />
							</tr>
						</tbody>
					</table>
				</div>
			</section>

		</section>
	);
};

const JobCostReviewChangeOrder = ({ accountingIdRow, approved, approveRow, accountManagerName, changeOrder, coordinatorName, fetchReport, job, operationsNote, rejectionNoteRow, summary, submitButton }: any) => {
	const budgetValues = summary.extensionItems.filter(item => item.group === 'accounting');
	const parentContractLabel = `Original ${globalRelay.config!.getString('Accounting ID', 'job')}`;
	const amount = summary.grandTotalContract;
	const appliedPayments = job.getSystemGeneratedPayments(changeOrder.key);
	const transactionRows: JSX.Element[] = [];
	const budgetValueRows = _.map(budgetValues || [], (v, i) => (<tr key={i}>
		<td />
		<td><strong>{v.itemNote}</strong></td>
		<td>{util.formatAsCurrency(v.type === 'budget-cost' ? v.cost : v.sellPrice)}</td>
		<td />
	</tr>));

	if (appliedPayments.length) {
		const payment = appliedPayments[0];

		transactionRows.push(<tr key="method">
			<td />
			<td><strong>Payment Method</strong></td>
			<td>{_.startCase(payment.transactionMethod)}</td>
			<td />
		</tr>);

		transactionRows.push(<tr key="reference">
			<td />
			<td><strong>Transaction Reference</strong></td>
			<td>{_.startCase(payment.transactionReference)}</td>
			<td />
		</tr>);
	}

	return (
		<section className="jobCostFinalize">
			{ /* Final Details */}
			<section>
				<header>
					<span>REVIEW</span>
					<div className="actions">
						<AsyncButton className={!approved ? 'disabled' : ''} asyncAction={() => fetchReport('changeorder/accounting', reportActions.changeAccountingSummaryShaper)} label="Print Selected" />
					</div>
				</header>
				<div className="inner compact">
					<table cellPadding="0" cellSpacing="0">
						<tbody>
							<tr>
								<td />
								<td><strong>{changeOrder.type === 'change-order' ? 'Change Order Name' : 'Billing Adjustment Name'}</strong></td>
								<td>{changeOrder.name}</td>
								<td />
							</tr>
							<tr>
								<td />
								<td><strong>Reason</strong></td>
								<td>{changeOrder.description}</td>
								<td />
							</tr>
							<tr>
								<td />
								<td><strong>Bill or Pay To</strong></td>
								<td>{changeOrder.transactionAccount}</td>
								<td />
							</tr>
							<tr>
								<td />
								<td><strong>Amount</strong></td>
								<td>{util.formatAsCurrency(amount)}</td>
								<td />
							</tr>
							{transactionRows}
							<tr>
								<td />
								<td><strong>Designer</strong></td>
								<td>{accountManagerName}</td>
								<td />
							</tr>
							<tr>
								<td />
								<td><strong>Coordinator</strong></td>
								<td>{coordinatorName}</td>
								<td />
							</tr>
							<tr className="">
								<td />
								<td><strong>Notes for Operations</strong></td>
								<td>{operationsNote || '-'}</td>
								<td />
							</tr>
							{budgetValueRows}
							<tr>
								<td />
								<td><strong>{parentContractLabel}</strong></td>
								<td>{summary.jobCost.accountingId}</td>
								<td />
							</tr>
							{accountingIdRow}
							{approveRow}
							{rejectionNoteRow}
							<tr>
								<td />
								<td />
								<td> {submitButton}</td>
								<td />
							</tr>
						</tbody>
					</table>
				</div>
			</section>

		</section>
	);
};

export default class JobCostEditReview extends React.Component<any, any> {
	constructor(props) {
		super(props);

		this.state = this.getStateFromProps(props);

		this.handleApproveChange = this.handleApproveChange.bind(this);
		this.handleNoteChange = this.handleNoteChange.bind(this);
		this.handleAccountingIdChange = this.handleAccountingIdChange.bind(this);

		this.approveAction = this.approveAction.bind(this);
		this.rejectAction = this.rejectAction.bind(this);
	}

	UNSAFE_componentWillReceiveProps(newProps) {
		this.setState(this.getStateFromProps(newProps));
	}

	// (DR) provides an override point, initially for affinity-kitchens
	_getOptions() {
		return {
			approvePrompt: 'Is everything complete, correct and approved by the customer?',
			exposeRejectionNote: true,
			rejectButtonLabel: 'Reject'
		};
	}

	getStateFromProps(props) {
		const { store } = componentServices;
		const { jobCost } = props;
		const state = _.assign({ operationsNote: '', installerNote: '', rejectionNote: '' }, this.state);
		const job = getModel({ store, collection: ModelCollection.jobs, key: jobCost.jobKey });
		const builder = (job && job.builderKey) ? getModel({ store, collection: ModelCollection.contractors, key: job.builderKey }): undefined;
		const accountManager = (job && job.accountManagerKey) ? getModel({ store, collection: ModelCollection.employees, key: job.accountManagerKey }) : undefined;
		const coordinator = (job && job.coordinatorKey) ? getModel({ store, collection: ModelCollection.employees, key: job.coordinatorKey }) : undefined;
		const globalConfig = globalRelay.config!;

		if (state.jobCostKey !== jobCost.key || state.jobCostItemVersion !== jobCost.itemVersion) {
			const proposedDeliveryItem = _.find(jobCost.scheduleItems, { type: jobCostScheduleItemFactory.TYPES.PROPOSED_DELIVERY });
			const reviewTarget = jobCost.currentChangeOrder || jobCost;
			const jobCosts = getCollection({ store, collection: ModelCollection.jobCosts}).filter(jc => jc.jobKey === jobCost.jobKey && jc.key !== jobCost.key && jc.currentStatus !== 'rejected');
			let firstJobContract;

			jobCosts.forEach(jc => {
				const testStatus = jc.getMostRecentStatusByStatus('approved') || jc.getMostRecentStatusByStatus('pending-approval');

				if (testStatus && (!firstJobContract || !firstJobContract.statusDate || testStatus.dateCreated < firstJobContract.statusDate || (firstJobContract.status === 'pending-approval' && testStatus.status === 'approved'))) {
					firstJobContract = { key: jc.key, statusDate: (testStatus && testStatus.dateCreated), status: (testStatus && testStatus.status) };
				}
			});

			if (reviewTarget.currentStatus !== 'pending-approval') {
				const reviewStatus = reviewTarget.getMostRecentStatusByStatus('approved') || reviewTarget.getMostRecentStatusByStatus('rejected');
				if (reviewStatus) {
					state.approved = (reviewStatus.status === 'approved') ? '1' : '0';
					state.rejectionNote = reviewStatus.description;
				}
			}

			state.scopeOfWork = new ScopeOfWork(jobCost, job);

			state.changeOrder = jobCost.currentChangeOrder;
			state.operationsNote = jobCost.getNoteContent(jobCostNoteFactory.NOTE_TYPES.CONTRACT_FOR_OPERATIONS);
			state.installerNote = jobCost.getNoteContent(jobCostNoteFactory.NOTE_TYPES.CONTRACT_FOR_INSTALLER);
			state.proposedDeliverDate = proposedDeliveryItem && util.dateHelper.formatDate(proposedDeliveryItem.date, { userFriendly: true });
			state.accountingId = jobCost.currentChangeOrder ? jobCost.currentChangeOrder.accountingId : jobCost.accountingId;

			state.job = job;
			state.jobCostKey = jobCost.key;
			state.jobCostItemVersion = jobCost.itemVersion;
			state.firstJobContract = firstJobContract && jobCosts.get(firstJobContract.key);
		}

		state.accountManagerName = accountManager && accountManager.fullName;
		state.coordinatorName = coordinator && coordinator.fullName;

		state.jobTypeName = enums.businessSegments.getName(job.businessSegment);
		state.builder = builder;


		if (job.businessSegment === enums.businessSegments.Retail) {
			if (jobCost.getLineItemsByType('subtrade').length) {
				state.jobTypeName = globalConfig.getString('Ren-L');
			}
			else if (builder) {
				state.jobTypeName = job.isRenovation ? globalConfig.getString('Ren-C') : globalConfig.getString('Ret-C');
			}
			else {
				state.jobTypeName = job.isRenovation ? globalConfig.getString('Ren-H') : globalConfig.getString('Ret-H');
			}
		}

		if (!builder && job.builderKey) {
			contractorActions.fetchContractor({ key: job.builderKey });
		}

		if (!accountManager && job.accountManagerKey) {
			employeeActions.fetchEmployee({ key: job.accountManagerKey });
		}

		if (!coordinator && job.coordinatorKey) {
			employeeActions.fetchEmployee({ key: job.coordinatorKey });
		}

		return state;
	}

	handleApproveChange(newButton) {
		const { jobCost } = this.props;
		const reviewTarget = jobCost.currentChangeOrder || jobCost;

		if (reviewTarget.currentStatus !== 'pending-approval') { return; }

		// set state to force a re-render
		this.setState(previousState => {
			const newState = _.assign({}, previousState);

			newState.approved = newButton.value;

			return newState;
		});
	}

	handleNoteChange(e) {
		const newVal = e.type === 'blur' ? valueFilters.clean(e.target.value) : e.target.value;
		// set state to force a re-render
		this.setState(previousState => {
			const newState = _.assign({}, previousState);

			newState.rejectionNote = newVal;

			return newState;
		});
	}

	handleAccountingIdChange(e) {
		const { jobCost, updateModel } = this.props;
		const reviewTarget = jobCost.currentChangeOrder || jobCost;
		const newVal = e.type === 'blur' ? valueFilters.clean(e.target.value) : e.target.value;

		if (reviewTarget.getMostRecentStatusByStatus('approved')) {
			updateModel(jc => {
				const contract = jc.proxiedContract || jc;
				const updateTarget = jc.currentChangeOrder || contract;

				updateTarget.accountingId = newVal;
			}, { autoSave: e.type === 'blur' });
		}
		else if (reviewTarget.currentStatus === 'pending-approval') {
			// set state to force a re-render
			this.setState(previousState => {
				const newState = _.assign({}, previousState);

				newState.accountingId = newVal;

				return newState;
			});
		}
	}

	async approveAction() {
		const saveState = componentServices.store.getIn(['viewState', 'globalSave', 'state']);
		const { jobCost, summary } = this.props;
		const reviewTarget = jobCost.currentChangeOrder || jobCost;
		const { approved, accountingId } = this.state;
		const options = { autoSave: true };
		const globalConfig = globalRelay.config!.withVariant(jobCost.companyVariant);

		if (saveState !== 0 || approved !== '1' || !accountingId || reviewTarget.currentStatus !== 'pending-approval') { return; }

		const job = componentServices.store.getIn(['model', 'jobs', jobCost.jobKey]);

		if(!reviewTarget.preorderSubmittedDate) { // this indicates that the preorder screen was used so the accounting id has already been set
			const jobsByAccountingId = await searchJobs({ accountingId });

			if(jobsByAccountingId.length) {
				pushAlert({ type: 'error', message: `This ${globalRelay.config!.getString('Accounting ID', 'job')} has already been used.`, isCloseable: true });
				
				return;
			}
		}
		
		const prePromise = globalConfig.jobCostSettings.publishOnReview ? reportActions.fillReportData({ summaries: [summary], job }) : Promise.resolve();
		const reportResult = await prePromise;

		await globalRelay.executeGlobalFunction(WellKnownGlobalFunction.GENERATE_APPROVED_CONTRACT_REPORTS, { target: reviewTarget, job, summary });

		// be sure we have the current version of the store
		const alerts = store().getIn(['viewState', 'alerts']);

		if(!alerts.size) {

			this.props.updateModel(jc => {
				const contract = jc.proxiedContract || jc;
				const updateTarget = jc.currentChangeOrder || contract;

				if (updateTarget.getModelName() === 'JobCost') {
					updateTarget.type = 'contract';
				}
				updateTarget.statusLog.add({ status: 'approved' });
				updateTarget.accountingId = accountingId;

				// be sure the accounting id and type are correctly published to CMS1 if we're publishing to CMS1
				if (reportResult) {
					reportResult.data.jobCosts[0].accountingId = accountingId;
					reportResult.data.jobCosts[0].type = updateTarget.type;

					(options as any).extraneousPatch = [{
						op: 'replace',
						path: '../publish/' + (jc.currentChangeOrder ? 'change' : 'contract'),
						value: reportResult
					}];
				}
			}, options);
		}
	}

	rejectAction() {
		const saveState = componentServices.store.getIn(['viewState', 'globalSave', 'state']);
		const { jobCost, summary } = this.props;
		const reviewTarget = jobCost.currentChangeOrder || jobCost;
		const { approved, rejectionNote } = this.state;
		const options = { autoSave: true };

		if (saveState !== 0 || approved !== '0' || !rejectionNote || reviewTarget.currentStatus !== 'pending-approval') { return; }

		this.props.updateModel(jc => {
			const contract = jc.proxiedContract || jc;
			const updateTarget = jc.currentChangeOrder || contract;
			const changeOrderKey = jc.currentChangeOrder && jc.currentChangeOrder.key;

			updateTarget.statusLog.add({ status: 'rejected', description: rejectionNote });

			summary.eraseCalculatedItems(contract, changeOrderKey);
			summary.eraseStats(contract, changeOrderKey);

			// let note = _.find(contract.notes, { type: jobCostNoteFactory.NOTE_TYPES.CONTRACT_FOR_OPERATIONS, changeOrderKey: changeOrderKey });
			// if (note) { contract.notes.delete(note.key); }

			// note = _.find(contract.notes, { type: jobCostNoteFactory.NOTE_TYPES.CONTRACT_FOR_INSTALLER, changeOrderKey: changeOrderKey });
			// if (note) { contract.notes.delete(note.key); }

			if (updateTarget.getModelName() === 'JobCost') {
				contract.paymentTerms = [];
				// scopeOfWork.erase(contract);

				const scheduleItem = _.find(contract.scheduleItems, { type: jobCostScheduleItemFactory.TYPES.PROPOSED_DELIVERY });
				if (scheduleItem) { contract.scheduleItems.delete(scheduleItem.key); }

				// contract.submittedSummaryData = undefined;
			}
			else {
				// clear the payment terms for just this change order
				_.forEach(jc.paymentTerms, ti => jc.paymentTerms.delete(ti.key));
			}
		}, options);
	}

	builderAllowed() {
		const { jobCost } = this.props;
		const { builder, job } = this.state;
		const reviewTarget = jobCost.currentChangeOrder || jobCost;
		const payment = _.first(_.orderBy(job.getSystemGeneratedPayments(reviewTarget.key), ['sortIndex']));
		const paymentPasses = jobCost.currentChangeOrder && (!payment || ['account', 'purchase-order'].indexOf(payment.transactionMethod) === -1);

		return !builder || builder.currentStatus !== 'locked' || paymentPasses;
	}

	render() {
		const { fetchReport, jobCost, summary } = this.props;
		const reviewTarget = jobCost.currentChangeOrder || jobCost;
		const globalConfig = globalRelay.config!.withVariant(jobCost.companyVariant);
		const { accountingId, approved, firstJobContract, rejectionNote } = this.state;
		const { approvePrompt, exposeRejectionNote, rejectButtonLabel } = this._getOptions();
		const accountingIdLabel = globalConfig.getString('Accounting ID', 'job');
		const workflowConfig = globalConfig.jobCostSettings.workflow;
		const includedWorkflowKeys = (workflowConfig && workflowConfig.includedKeys) || [];
		const supportsPreOrder = includedWorkflowKeys.indexOf('pre-order') !== -1;
		const resolvedAccountingId = reviewTarget.getMostRecentStatusByStatus('approved') ? reviewTarget.accountingId : accountingId;

		const approveButtons = [
			{ classNames: ['icon yes'], isSelected: (approved === '1'), value: '1' },
			{ classNames: ['icon no'], isSelected: (approved === '0'), value: '0' }
		];
		const approveRow = (<tr className={(approved === '1') ? 'yes' : (approved === '0') ? 'no' : ''} key="approveRadio">
			<td />
			<td><strong>{approvePrompt}</strong></td>
			<td>
				<Radio buttons={approveButtons} onChange={this.handleApproveChange} />
			</td>
			<td />
		</tr>);
		const rejectionNoteRow = approved === '0' && exposeRejectionNote ? (<tr className={reviewTarget.currentStatus !== 'pending-approval' ? 'disabled' : ''}>
			<td />
			<td><strong>Notes for Salesperson to make corrections</strong></td>
			<td><textarea style={{ height: '80px', minHeight: 'auto' }} value={rejectionNote} onChange={this.handleNoteChange} onBlur={this.handleNoteChange} /></td>
			<td />
		</tr>) : null;

		const accountingIdRow = (<tr className={(!approved && reviewTarget.currentStatus !== 'pending-approval') || supportsPreOrder ? 'disabled' : ''}>
			<td />
			<td><strong>{accountingIdLabel}</strong></td>
			<td><input type="text" className="" placeholder={`Enter the ${accountingIdLabel}`} autoCapitalize="" autoCorrect="off" value={util.reactVal(resolvedAccountingId)} onChange={supportsPreOrder ? undefined : this.handleAccountingIdChange} onBlur={supportsPreOrder ? undefined : this.handleAccountingIdChange} /></td>
			<td />
		</tr>);

		const submitButtonClasses = approved === '0' ? ['action', 'iconLabel', 'reject'] : ['action', 'iconLabel', 'finalize'];
		const builderPasses = this.builderAllowed();

		if (!builderPasses || typeof approved === 'undefined' || (approved === '0' && !rejectionNote && exposeRejectionNote) || (approved === '1' && !accountingId) || reviewTarget.currentStatus !== 'pending-approval') {
			submitButtonClasses.push('disabled');
		}

		const submitButtonText = approved === '0' ? rejectButtonLabel : 'Accept';
		const submitButton = (<AsyncButton className={submitButtonClasses.join(' ')} asyncAction={approved === '1' ? this.approveAction : this.rejectAction} label={submitButtonText} />);

		return (jobCost.currentChangeOrder)
			? (<JobCostReviewChangeOrder accountingIdRow={accountingIdRow} approveRow={approveRow} rejectionNoteRow={rejectionNoteRow} submitButton={submitButton} summary={summary} fetchReport={fetchReport} {...this.state} />)
			: (<JobCostReviewContract accountingIdRow={accountingIdRow} approveRow={approveRow} firstJobContract={firstJobContract} rejectionNoteRow={rejectionNoteRow} submitButton={submitButton} summary={summary} {...this.state} />);
	}
}
